import cytoscape from "cytoscape";
// @ts-expect-error - nodeHtmlLabel est une extension de Cytoscape qui n'a pas de types TypeScript
import nodeHtmlLabel from "cytoscape-node-html-label";
import html2canvas from "html2canvas";
import { useCallback, useEffect, useRef } from "react";

import { EReputationStatus } from "src/core/constants/eReputation";
import { useFolder } from "src/core/contexts/FolderContext";
import { useFolderGraphSync } from "src/core/contexts/FolderGraphSyncContext";
import { ElementInputValue } from "src/core/gql/graphql";
import { getColor, getCompanyIconSvg, getPersonIconSvg } from "src/core/utils/graph";

// Initialiser l'extension une seule fois
if (!cytoscape.prototype.hasInitialised) {
  cytoscape.use(nodeHtmlLabel);
  cytoscape.prototype.hasInitialised = true;
}

type NodeData = {
  id: string;
  isCentral?: boolean;
  counter?: number;
  image: string;
  label: string;
  isPepSanction?: boolean;
};

const getInputValue = (inputValues: ElementInputValue[], fieldCode: string): string => {
  return inputValues.find((iv) => iv.inputField.fieldCode === fieldCode)?.stringValue || "";
};

export const useFolderGraph = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { folderFile, tabCounters, mappedStakeholders, isReadOnly, mappedEntities } = useFolder();
  const {
    isCentralSelected,
    selectedStakeholderForEdit,
    setSelectedStakeholderForEdit,
    setSelectedTab,
    setOpenAddModal,
    setOpenDeleteModal,
    setSelectedStakeholder,
    setIsCentralSelected,
    setSelectedEntity,
    setOpenEntityDetailsModal,
    setOpenUpdateModal,
  } = useFolderGraphSync();

  const getNodeImage = (
    node: { beneficiaryOwner: boolean; isLegalRepresentative: boolean },
    isSelected: boolean
  ) => {
    const color = isSelected ? getColor("--primary") : getColor("--foreground");
    if (node.beneficiaryOwner && node.isLegalRepresentative) return getPersonIconSvg(color, "RLBE");
    if (node.beneficiaryOwner) return getPersonIconSvg(color, "BE");
    if (node.isLegalRepresentative) return getPersonIconSvg(color, "RL");
    return getPersonIconSvg(color, "RLBE");
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const central = folderFile?.folderFile?.fileCustomerFolder?.stakeholders?.find(
      (s) => s.isFolderCustomer
    );
    const others = folderFile?.folderFile?.fileCustomerFolder?.stakeholders?.filter(
      (s) => !s.isFolderCustomer
    );

    if (!central || !containerRef.current || !others) return;

    const elements = [
      {
        data: {
          id: central.entity.id,
          label: getInputValue(central.inputValues as ElementInputValue[], "name"),
          image: getCompanyIconSvg(
            isCentralSelected ? getColor("--primary") : getColor("--foreground")
          ),
          isCentral: true,
          counter: tabCounters.corporation,
          isPepSanction: false,
        },
        classes: "central",
      },
      ...others.map((stakeholder) => ({
        data: {
          id: stakeholder.entity.id,
          label:
            `${getInputValue(
              stakeholder.inputValues as ElementInputValue[],
              "firstName"
            )} ${getInputValue(
              stakeholder.inputValues as ElementInputValue[],
              "lastName"
            )}`.trim() || "",
          image: getNodeImage(
            stakeholder,
            stakeholder.entity.id === selectedStakeholderForEdit?.id
          ),
          isCentral: false,
          counter: tabCounters.stakeholdersDetails[stakeholder.entity.id],
          isPepSanction:
            mappedEntities.find((e) => e.id === stakeholder.entity.id)?.pepStatus ===
              EReputationStatus.CONFIRMED ||
            mappedEntities.find((e) => e.id === stakeholder.entity.id)?.sanctionStatus ===
              EReputationStatus.CONFIRMED,
        },
      })),
      ...others.map((stakeholder) => ({
        data: {
          source: stakeholder.entity.id,
          target: central.entity.id,
        },
      })),
    ];

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      layout: {
        name: "concentric",
        padding: 30,
        fit: false,
        minNodeSpacing: 100,
      },
      style: [
        {
          selector: "core",
          style: {
            "active-bg-color": "transparent",
            "active-bg-opacity": 0,
            "active-bg-size": 0,

            "selection-box-color": "transparent",
            "selection-box-border-color": "transparent",
            "selection-box-border-width": 0,
            "selection-box-opacity": 0,

            "outside-texture-bg-color": "transparent",
            "outside-texture-bg-opacity": 0,
          },
        },
        {
          selector: "node",
          style: {
            width: 80,
            height: 80,
            "background-opacity": 0,
            "border-opacity": 0,
            "overlay-color": "transparent",
            "overlay-padding": 0,
            "overlay-opacity": 0,
            events: "no",
          },
        },
        {
          selector: "node.touch",
          style: {
            events: "yes",
          },
        },
        {
          selector: "edge",
          style: {
            width: 2,
            "line-color": getColor("--foreground"),
            "target-arrow-color": getColor("--foreground"),
            "target-arrow-shape": "triangle",
            "curve-style": "bezier",
            "z-index": 0,
          },
        },
      ],
    });
    others.length < 10 ? cy.zoom(0.8) : others.length > 15 ? cy.zoom(0.4) : cy.zoom(0.65);

    const TOP_OFFSET_PX = 50;

    const alignTop = () => {
      const bb = cy.elements().boundingBox();
      const zoom = cy.zoom();
      const w = container.clientWidth;

      const centerModelX = (bb.x1 + bb.x2) / 2;
      const panX = w / 2 - centerModelX * zoom;

      const panY = TOP_OFFSET_PX - bb.y1 * zoom;

      cy.pan({ x: panX, y: panY });
    };

    cy.one("layoutstop", alignTop);
    cy.on("resize", alignTop);

    alignTop();

    cy.nodeHtmlLabel([
      {
        query: "node",
        halign: "center",
        valign: "center",
        halignBox: "center",
        valignBox: "center",
        tpl: function (data: NodeData) {
          const selected =
            data.isCentral && isCentralSelected
              ? true
              : data.id === selectedStakeholderForEdit?.id
              ? true
              : false;
          const isCentral = data.isCentral;
          const counter = data.counter ?? 0;

          return `
            <div 
              class="group relative w-[80px] h-[80px] rounded-full flex flex-col items-center justify-center 
                     border
                     ${
                       selected
                         ? "border-primary bg-primary/10"
                         : "border-border bg-popover hover:border-foreground hover:cursor-pointer"
                     } 
                     "
              data-node-id="${data.id}"
            >
              <img src="${data.image}" />
              <span class="absolute top-[92px] w-max text-base text-center text-foreground bg-background-secondary">${
                data.label
              }</span>
    
              ${
                counter === 0
                  ? `<div class="absolute top-[-10px] right-0 text-blue-completed">
                 <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10" cy="10" r="9.25" fill="currentColor" stroke="#FBFCFF" stroke-width="1.5"/>
                  <path d="M10 20C11.9778 20 13.9112 19.4135 15.5557 18.3147C17.2002 17.2159 18.4819 15.6541 19.2388 13.8268C19.9957 11.9996 20.1937 9.98891 19.8078 8.0491C19.422 6.10929 18.4696 4.32746 17.0711 2.92894C15.6725 1.53041 13.8907 0.578004 11.9509 0.192152C10.0111 -0.1937 8.00043 0.00433279 6.17316 0.761209C4.3459 1.51809 2.78412 2.79981 1.6853 4.4443C0.586489 6.08879 0 8.02219 0 10C0 12.6522 1.05357 15.1957 2.92893 17.0711C4.8043 18.9464 7.34783 20 10 20ZM10 1.66667C11.6482 1.66667 13.2593 2.15541 14.6297 3.07109C16.0002 3.98677 17.0683 5.28826 17.699 6.81097C18.3297 8.33369 18.4948 10.0092 18.1732 11.6258C17.8517 13.2423 17.058 14.7271 15.8926 15.8926C14.7271 17.058 13.2423 17.8517 11.6257 18.1732C10.0092 18.4948 8.33369 18.3297 6.81097 17.699C5.28825 17.0683 3.98676 16.0002 3.07109 14.6298C2.15541 13.2593 1.66667 11.6482 1.66667 10C1.66667 7.78987 2.54464 5.67025 4.10744 4.10745C5.67025 2.54464 7.78986 1.66667 10 1.66667Z" fill="currentColor"/>
                  <path d="M7.83887 13.2116C7.91599 13.2892 8.00775 13.3508 8.10883 13.3928C8.20991 13.4347 8.31831 13.4562 8.42776 13.456C8.63076 13.4631 8.82901 13.3937 8.98331 13.2616L15.2778 8.01714C15.4345 7.87202 15.5296 7.67224 15.5433 7.45906C15.557 7.24587 15.4883 7.03556 15.3514 6.87157C15.2145 6.70757 15.0198 6.60241 14.8076 6.57781C14.5954 6.55321 14.3819 6.61104 14.2111 6.73937L8.47776 11.4949L5.86665 8.88937C5.70867 8.74217 5.49973 8.66203 5.28384 8.66584C5.06795 8.66965 4.86197 8.7571 4.70929 8.90979C4.55661 9.06247 4.46915 9.26845 4.46534 9.48434C4.46153 9.70023 4.54167 9.90917 4.68887 10.0671L7.83887 13.2116Z" fill="#FBFCFF"/>
                </svg>
                  </div>`
                  : `<div class="absolute top-0 right-[5px] w-20 h-20 bg-background-secondary border border-red-medium rounded-full text-red-medium font-medium text-base flex items-center justify-center">!</div>`
              }${
                data.isPepSanction
                  ? `<button class="absolute z-[50] pointer-events-auto left-[-12px] w-[24px] h-[24px] bg-red-medium hover:bg-red-xlight rounded-full text-red-xlight hover:cursor-pointer hover:text-red-medium font-medium flex items-center justify-center flex-col text-[10px] leading-[8px]" data-action="alerte" data-id="${data.id}">
                  <span class="underline mt-[3px]">PEP</span>
                  <span class="mt-[2px]">S</span>
                  </button>`
                  : ""
              }
              <div class="absolute inset-0 flex items-center justify-center z-[100] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity" data-touch-buttons>
              
              ${
                !isReadOnly && !isTouchDevice && isCentral
                  ? `<button class="absolute left-[90px] top-1/2 -translate-y-1/2 w-30 h-30 rounded-full hover:bg-blue-primary-light hover:text-primary flex items-center justify-center border border-primary bg-primary text-background" data-action="add" data-id="${data.id}">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.712 5.712H8.288V1.288C8.288 0.5775 7.71225 0 7 0C6.2895 0 5.712 0.57575 5.712 1.288V5.712H1.288C0.57575 5.712 0 6.2895 0 7C0 7.7105 0.57575 8.288 1.288 8.288H5.712V12.712C5.712 13.4225 6.28775 14 7 14C7.7105 14 8.288 13.4243 8.288 12.712V8.288H12.712C13.4225 8.288 14 7.71225 14 7C14 6.2895 13.4243 5.712 12.712 5.712Z" fill="currentColor"/>
                    </svg>
                  </button>`
                  : !isReadOnly && !isTouchDevice && !isCentral
                  ? `<button class="absolute z-[101] left-[90px] top-1/2 -translate-y-1/2 w-30 h-30 rounded-full bg-background border border-muted text-muted hover:text-foreground flex items-center justify-center hover:border-foreground" data-action="delete" data-id="${data.id}">
                    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 0H9H9.375L9.625 0.34375L11.0625 2.5H11.5H13H14V4H13V14.5V16H11.5H2.5H1V14.5V4H0V2.5H1H2.5H2.90625L4.375 0.34375L4.59375 0H5ZM9.25 2.5L8.59375 1.5H5.375L4.71875 2.5H9.25ZM2.5 14.5H11.5V4H2.5V14.5ZM5 6V12.5V13H4V12.5V6V5.5H5V6ZM7.5 6V12.5V13H6.5V12.5V6V5.5H7.5V6ZM10 6V12.5V13H9V12.5V6V5.5H10V6Z" fill="currentColor"/>
                    </svg>
                  </button>`
                  : ""
              }
              </div>
            </div>
          `;
        },
      },
    ]);

    const handleHtmlButtonClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const button = target.closest("button[data-action]") as HTMLButtonElement | null;
      if (button) {
        e.stopImmediatePropagation();
        e.preventDefault();

        const action = button.dataset.action;
        const nodeId = button.dataset.id;

        if (!action || !nodeId) return;

        if (action === "add") {
          setOpenAddModal(true);
          setIsCentralSelected(false);
          setSelectedStakeholder(undefined);
        } else if (action === "delete") {
          const stakeholder = mappedStakeholders.find((s) => s.id === nodeId);
          if (stakeholder) {
            setSelectedStakeholder(stakeholder);
            setOpenDeleteModal(true);
          }
        } else if (action === "alerte") {
          const entity = mappedEntities.find((e) => e.id === nodeId);
          if (entity) {
            setSelectedTab("alerte");
            setSelectedEntity(entity);
            setOpenEntityDetailsModal(true);
          }
        }
        return;
      }
      const nodeDiv = target.closest("[data-node-id]") as HTMLElement | null;
      if (nodeDiv) {
        const nodeId = nodeDiv.dataset.nodeId;
        if (!nodeId) return;
        setOpenUpdateModal(true);
        setSelectedStakeholderForEdit(mappedStakeholders.find((s) => s.id === nodeId));
        const central = folderFile?.folderFile?.fileCustomerFolder?.stakeholders?.find(
          (s) => s.isFolderCustomer
        );
        setSelectedTab(nodeId === central?.entity.id ? "entreprise" : "stakeholders");
        setIsCentralSelected(nodeId === central?.entity.id);
      }
    };

    // Détection tactile
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    // Ajouter la classe touch aux nœuds sur les appareils tactiles
    if (isTouchDevice) {
      cy.ready(() => {
        cy.nodes().addClass("touch");
      });
    }

    container.addEventListener("click", handleHtmlButtonClick);

    // Gestionnaire pour les événements tactiles (seulement pour les alertes)
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const button = target.closest("button[data-action='alerte']") as HTMLButtonElement | null;
      if (button) {
        e.preventDefault();
        e.stopPropagation();

        const nodeId = button.dataset.id;
        if (!nodeId) return;

        const entity = mappedEntities.find((e) => e.id === nodeId);
        if (entity) {
          setSelectedTab("alerte");
          setSelectedEntity(entity);
          setOpenEntityDetailsModal(true);
        }
      }
    };

    if (isTouchDevice) {
      container.addEventListener("touchstart", handleTouchStart, { passive: false });
    }

    return () => {
      container.removeEventListener("click", handleHtmlButtonClick);
      if (isTouchDevice) {
        container.removeEventListener("touchstart", handleTouchStart);
      }
      cy.removeListener("resize", alignTop);
    };
  }, [
    folderFile,
    setSelectedTab,
    mappedStakeholders,
    setOpenAddModal,
    setOpenDeleteModal,
    setSelectedStakeholder,
    tabCounters.corporation,
    tabCounters.stakeholdersDetails,
    isReadOnly,
    isCentralSelected,
    selectedStakeholderForEdit,
    setSelectedStakeholderForEdit,
    mappedEntities,
    setSelectedEntity,
    setOpenEntityDetailsModal,
    setIsCentralSelected,
    setOpenUpdateModal,
  ]);

  const getPng = useCallback(async (): Promise<string | null> => {
    const container = containerRef.current;
    if (!container) return null;

    try {
      // 1) capture avec fond transparent pour que l'alpha serve au crop
      const canvas = await html2canvas(container, {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: null,
      });

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        // fallback : fond blanc direct
        return drawOnWhiteBackground(canvas).toDataURL("image/png");
      }

      // 2) crop sur pixels non-transparents (donc sans le "vide")
      const bbox = getNonTransparentBoundingBox(ctx, canvas.width, canvas.height);
      const cropped = bbox ? cropCanvasToBox(canvas, bbox, 24) : canvas;

      // 3) recompose sur fond blanc (évite transparence dans le PDF)
      const finalCanvas = drawOnWhiteBackground(cropped);

      return finalCanvas.toDataURL("image/png");
    } catch {
      return null;
    }
  }, []);

  return { containerRef, getPng };
};

type BoundingBox = {
  x: number;
  y: number;
  w: number;
  h: number;
};

const getNonTransparentBoundingBox = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number
): BoundingBox | null => {
  const { data } = ctx.getImageData(0, 0, w, h);

  let minX = w;
  let minY = h;
  let maxX = 0;
  let maxY = 0;

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const idx = (y * w + x) * 4;
      const alpha = data[idx + 3];

      if (alpha > 0) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (minX === w || minY === h) return null;

  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
};

const cropCanvasToBox = (
  source: HTMLCanvasElement,
  bbox: BoundingBox,
  paddingPx: number
): HTMLCanvasElement => {
  const pad = Math.max(0, paddingPx);

  const sx = Math.max(0, bbox.x - pad);
  const sy = Math.max(0, bbox.y - pad);
  const sw = Math.min(source.width - sx, bbox.w + pad * 2);
  const sh = Math.min(source.height - sy, bbox.h + pad * 2);

  const target = document.createElement("canvas");
  target.width = sw;
  target.height = sh;

  const tctx = target.getContext("2d");
  if (!tctx) return source;

  tctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
  return target;
};

const drawOnWhiteBackground = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const target = document.createElement("canvas");
  target.width = source.width;
  target.height = source.height;

  const ctx = target.getContext("2d");
  if (!ctx) return source;

  // fond blanc (PDF-friendly)
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, target.width, target.height);

  ctx.drawImage(source, 0, 0);
  return target;
};
